import { useState, useEffect, useRef } from 'react';
import { HardDrive, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import { useUIStore } from '@/stores/ui-store';

/**
 * Storage: how much of the plan's allowance the site uses, and the extra
 * storage add-on (+50 GB / +250 GB / +1 TB) on top of it.
 *
 * Upload routes answer a full store with a message that sends the owner here
 * (Settings → Storage, `#storage`), so this card is the one place where
 * "you're out of room" turns into "here's more room".
 */

export interface StorageTier {
  slug: string;
  name: string;
  extraStorageGb: number;
  monthlyPriceCents: number;
  annualPriceCents: number;
}

export interface StorageStatus {
  usedBytes: number | null;
  limitGb: number | null;
  planGb: number | null;
  unmetered: boolean;
  planSlug: string | null;
  current: StorageTier | null;
  tiers: StorageTier[];
  blockedReason: 'no_subscription' | 'comped' | null;
}

const GB = 1024 * 1024 * 1024;

export function formatGb(bytes: number): string {
  const gb = bytes / GB;
  if (gb < 0.1) return `${Math.max(0, Math.round((bytes / (1024 * 1024)) * 10) / 10)} MB`;
  return `${gb < 10 ? gb.toFixed(1) : Math.round(gb).toLocaleString()} GB`;
}

export function formatExtra(gb: number): string {
  return gb >= 1000 && gb % 1000 === 0 ? `+${gb / 1000} TB` : `+${gb.toLocaleString()} GB`;
}

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export default function StorageSection() {
  const [data, setData] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const showToast = useUIStore((s) => s.addToast);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void load();
  }, []);

  // Upload errors link to /admin/settings#storage. The card loads after the
  // page, so the browser's own hash jump has nothing to land on — do it here.
  useEffect(() => {
    if (!loading && window.location.hash === '#storage') {
      anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading]);

  async function load() {
    setLoading(true);
    try {
      setData(await api.get<StorageStatus>('/admin/storage'));
    } catch {
      showToast('Could not load storage status', 'error');
    } finally {
      setLoading(false);
    }
  }

  async function change(slug: string | null) {
    setBusySlug(slug ?? 'remove');
    try {
      await api.put('/admin/storage/addon', { addon_slug: slug });
      showToast(slug ? 'Extra storage added' : 'Extra storage removed', 'success');
      await load();
    } catch (err: any) {
      showToast(err?.message || 'Could not change storage', 'error');
    } finally {
      setBusySlug(null);
    }
  }

  function confirmAndChange(tier: StorageTier | null) {
    const message = tier
      ? `Add ${tier.name} for ${dollars(tier.monthlyPriceCents)}/month (${dollars(tier.annualPriceCents)}/year on annual billing)? It's added to your plan right away and prorated on your next invoice.`
      : 'Remove extra storage? Your storage allowance drops back to your plan’s at once, and the unused time is credited on your next invoice.';
    if (window.confirm(message)) void change(tier?.slug ?? null);
  }

  return (
    <div id="storage" ref={anchorRef} className="scroll-mt-20">
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-ink">
          <HardDrive className="h-5 w-5" /> Storage
        </h3>

        {loading ? (
          <Loader2 className="mt-4 h-5 w-5 animate-spin text-sage-500" />
        ) : !data ? null : (
          <StorageBody
            data={data}
            busySlug={busySlug}
            onChange={confirmAndChange}
          />
        )}
      </Card>
    </div>
  );
}

function StorageBody({
  data,
  busySlug,
  onChange,
}: {
  data: StorageStatus;
  busySlug: string | null;
  onChange: (tier: StorageTier | null) => void;
}) {
  const { usedBytes, limitGb, planGb, current, tiers, blockedReason, unmetered } = data;
  const limitBytes = limitGb === null ? null : limitGb * GB;
  const usedPct = usedBytes !== null && limitBytes ? Math.min(100, (usedBytes / limitBytes) * 100) : 0;
  const nearlyFull = usedPct >= 90;

  return (
    <>
      {/* Usage */}
      <div className="mt-4">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-2xl font-semibold tabular-nums text-ink">
            {usedBytes === null ? '—' : formatGb(usedBytes)}
          </span>
          <span className="text-sm text-ink-muted">
            {unmetered || limitGb === null
              ? 'No storage limit on this site'
              : `of ${limitGb.toLocaleString()} GB`}
          </span>
        </div>
        {limitBytes !== null && !unmetered && (
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-sage-200">
            <div
              className={`h-full rounded-full ${nearlyFull ? 'bg-terra-500' : 'bg-sage-500'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>
        )}
        {current && planGb !== null && (
          <p className="mt-2 text-sm text-ink-muted">
            {planGb.toLocaleString()} GB with your plan, plus {formatExtra(current.extraStorageGb)} extra storage.
          </p>
        )}
        {nearlyFull && !current && (
          <p className="mt-2 flex items-center gap-1.5 text-sm text-terra-700">
            <AlertTriangle className="h-4 w-4" />
            You're nearly out of room. Add extra storage below, or remove files you no longer use.
          </p>
        )}
      </div>

      {/* Extra storage */}
      {!unmetered && tiers.length > 0 && (
        <div className="mt-6 border-t border-line pt-6">
          <h4 className="text-sm font-medium text-ink">Extra storage</h4>
          {blockedReason === 'comped' ? (
            <p className="mt-1 text-sm text-ink-muted">
              This site is on a complimentary plan. Ask Rumio support if it needs more room.
            </p>
          ) : blockedReason === 'no_subscription' ? (
            <p className="mt-1 text-sm text-ink-muted">
              Choose a plan for this site first — extra storage is added on top of it.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-ink-muted">
                Added to your plan's bill and prorated. One tier at a time; switch or remove whenever you like.
              </p>
              <ul className="mt-3 space-y-2">
                {tiers.map((tier) => {
                  const active = current?.slug === tier.slug;
                  const upgrade = !current || tier.extraStorageGb > current.extraStorageGb;
                  return (
                    <li
                      key={tier.slug}
                      className={`flex items-center justify-between gap-4 rounded-lg border p-4 ${
                        active ? 'border-sage-500 bg-sage-50' : 'border-line'
                      }`}
                    >
                      <div>
                        <p className="text-sm font-medium text-ink">{formatExtra(tier.extraStorageGb)}</p>
                        <p className="text-sm text-ink-muted">
                          {dollars(tier.monthlyPriceCents)}/month · {dollars(tier.annualPriceCents)}/year
                        </p>
                      </div>
                      {active ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busySlug !== null}
                          isLoading={busySlug === 'remove'}
                          onClick={() => onChange(null)}
                        >
                          Remove
                        </Button>
                      ) : (
                        <Button
                          variant={upgrade ? 'primary' : 'outline'}
                          size="sm"
                          disabled={busySlug !== null}
                          isLoading={busySlug === tier.slug}
                          onClick={() => onChange(tier)}
                        >
                          {!current ? 'Add' : upgrade ? 'Upgrade' : 'Switch'}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </>
  );
}
